export default {
  install(app) {
    app.directive('selectable', {
      beforeMount(el, binding) {
        const vm = binding.instance;

        const isSubTable = binding.value?.isSubTable || false;
        const SELECTED_CLASSES = ['bg-warning-subtle', 'shadow-none'];
        const state = {
          startRow: -1,
          startCol: -1,
          endRow: -1,
          endCol: -1,
          isSelecting: false,
          dragStarted: false,
          cmdSelected: new Set(),
          anchorRow: -1,
          anchorCol: -1,
        };
        el.__selectable__ = {
          onCopy,
          onClear,
        };
        el.__selectableState = state;

        function cellKey(r, c) {
          return `${r},${c}`;
        }

        function clearDragSelection() {
          state.startRow = state.startCol = state.endRow = state.endCol = -1;
        }

        function clearOtherTablesSelection() {
          const tables = document.querySelectorAll('.selectable');

          tables.forEach((table) => {
            if (table != el) {
              table.__selectable__.onClear();
            }
          });
        }

        function getGlobalRowIndex(tr) {
          // Get rows only from current table context (main or subtable)
          const rows = isSubTable ? el.querySelectorAll('tbody tr') : el.rows;
          return Array.prototype.indexOf.call(rows, tr);
        }

        function paint() {
          const { startRow, endRow, startCol, endCol, cmdSelected } = state;
          const minR = Math.min(startRow, endRow),
            maxR = Math.max(startRow, endRow),
            minC = Math.min(startCol, endCol),
            maxC = Math.max(startCol, endCol);

          for (const cell of getElements()) {
            if (cell.offsetParent === null) {
              continue;
            }
            const tr = cell.parentElement;
            const row = getGlobalRowIndex(tr);
            const col = cell.cellIndex;
            const key = cellKey(row, col);
            const inCmd = cmdSelected.has(key);
            const inDrag = row >= minR && row <= maxR && col >= minC && col <= maxC;
            if (inCmd || inDrag) {
              cell.classList.add(...SELECTED_CLASSES);
            } else {
              cell.classList.remove(...SELECTED_CLASSES);
            }
          }
        }

        function getElements(tr) {
          const target = tr ? tr : el;

          if (!isSubTable) {
            return target.querySelectorAll('td:not(.sub-table-td):not(.sub-table-cell), th:not(.sub-table-th)');
          }

          return target.querySelectorAll('td.sub-table-td, th.sub-table-th');
        }

        function isModifierKey(e) {
          // User is holding Cmd (Mac) or Ctrl (Windows)
          return e.metaKey || e.ctrlKey;
        }

        function onMouseDown(e) {
          const cell = e.target.closest('td,th');
          if (!cell) {
            return;
          }

          if (e.target.closest('.resize-handle, .column-name.handle, .drag-handle, .sort-handle')) {
            // Don't initiate drag selection
            state.isSelecting = false;
            return;
          }

          if (!el.contains(cell) || cell.closest('table') !== el) {
            state.isSelecting = false;
            return;
          }
          if (!isSubTable && cell.classList.contains('sub-table-cell')) {
            state.isSelecting = false;
            return;
          }

          if (isModifierKey(e)) {
            // Don't initiate drag selection
            state.isSelecting = false;
            return;
          }

          clearOtherTablesSelection();

          // prevent the browser’s default text selection behavior during mouse drag
          e.preventDefault();

          const row = getGlobalRowIndex(cell.parentElement);
          const col = cell.cellIndex;
          state.startRow = state.endRow = row;
          state.startCol = state.endCol = col;
          state.isSelecting = true;
          state.dragStarted = false;

          vm.changeSelectedCount(getSelectedCells());
        }

        function onMouseOver(e) {
          if (e.target.closest('.sub-table-block') && !isSubTable) {
            state.isSelecting = false;
            return;
          }

          if (!state.isSelecting) {
            return;
          }
          const cell = e.target.closest('td,th');
          if (!cell) {
            return;
          }

          const row = getGlobalRowIndex(cell.parentElement);
          const col = cell.cellIndex;
          if (row !== state.startRow || col !== state.startCol) {
            state.dragStarted = true;

            state.cmdSelected.clear();
          }
          state.endRow = row;
          state.endCol = col;
          paint();
          vm.changeSelectedCount(getSelectedCells());
        }

        function onMouseUp() {
          state.isSelecting = false;
        }

        function clearClosedSelection() {
          const selected = [...getElements()].filter((cell) =>
            SELECTED_CLASSES.every((cls) => cell.classList.contains(cls)),
          );
          if (!selected.length) {
            return;
          }

          selected.forEach((cell) => {
            if (cell.tagName.toLowerCase() === 'td') {
              const tr = cell.closest('tr');
              if (tr.style.display === 'none') {
                const row = getGlobalRowIndex(cell.parentElement);
                const col = cell.cellIndex;
                const key = cellKey(row, col);

                cell.classList.remove(...SELECTED_CLASSES);
                state.cmdSelected.delete(key);
              }
            }
          });
          vm.changeSelectedCount(getSelectedCells());
        }

        function onClick(e) {
          if (e.target.closest('.resize-handle, .column-name.handle, .drag-handle, .sort-handle')) {
            return;
          }

          const toggleTags = ['svg', 'polygon', 'path'];
          if (toggleTags.includes(e.target.tagName.toLowerCase())) {
            clearClosedSelection();
            vm.changeSelectedCount(getSelectedCells());
            return;
          }

          if (state.dragStarted) {
            state.dragStarted = false;
            clearDragSelection();
            vm.changeSelectedCount(getSelectedCells());
            return;
          }

          const cell = e.target.closest('td,th');
          if (!cell) {
            return;
          }

          if (!el.contains(cell) || cell.closest('table') !== el) {
            return;
          }
          if (!isSubTable && cell.classList.contains('sub-table-cell')) {
            return;
          }

          clearOtherTablesSelection();

          const row = getGlobalRowIndex(cell.parentElement);
          const col = cell.cellIndex;
          const key = cellKey(row, col);

          if (e.shiftKey && state.anchorRow >= 0) {
            state.cmdSelected.clear();
            const minR = Math.min(state.anchorRow, row);
            const maxR = Math.max(state.anchorRow, row);
            const minC = Math.min(state.anchorCol, col);
            const maxC = Math.max(state.anchorCol, col);
            for (let r = minR; r <= maxR; r++) {
              for (let c = minC; c <= maxC; c++) {
                state.cmdSelected.add(cellKey(r, c));
              }
            }
            clearDragSelection();
            paint();
            e.preventDefault();
            vm.changeSelectedCount(getSelectedCells());
            return;
          }

          if (!isModifierKey(e)) {
            // simple click on the only selected cell: deselect it
            if (state.cmdSelected.size === 1 && state.cmdSelected.has(key) && state.startRow === -1) {
              state.cmdSelected.clear();
              paint();
              e.preventDefault();
              vm.changeSelectedCount(getSelectedCells());
              return;
            }

            // simple click: select only this cell
            state.cmdSelected.clear();
            state.cmdSelected.add(key);
            state.anchorRow = row;
            state.anchorCol = col;
            clearDragSelection();
            paint();
            e.preventDefault();
            vm.changeSelectedCount(getSelectedCells());
            return;
          }

          if (state.cmdSelected.has(key)) {
            state.cmdSelected.delete(key); // deselect
          } else {
            state.cmdSelected.add(key); // select
          }
          state.anchorRow = row;
          state.anchorCol = col;
          clearDragSelection();
          paint();
          e.preventDefault();
          vm.changeSelectedCount(getSelectedCells());
        }

        function onDblClick(e) {
          if (!isModifierKey(e)) {
            return;
          }

          e.preventDefault();

          const cell = e.target.closest('td,th');
          if (!cell) {
            return;
          }

          if (!el.contains(cell) || cell.closest('table') !== el) {
            return;
          }

          if (!isSubTable && cell.classList.contains('sub-table-cell')) {
            return;
          }

          clearOtherTablesSelection();

          const row = getGlobalRowIndex(cell.parentElement);
          const col = cell.cellIndex;

          if (row === 0 && col === 0) {
            state.cmdSelected.clear();
            for (const cell of getElements()) {
              const r = getGlobalRowIndex(cell.parentElement);
              const c = cell.cellIndex;
              state.cmdSelected.add(cellKey(r, c));
            }
            clearDragSelection();
            paint();
          } else if (row === 0) {
            for (const cell of getElements()) {
              if (cell.cellIndex === col) {
                const r = getGlobalRowIndex(cell.parentElement);
                state.cmdSelected.add(cellKey(r, col));
              }
            }
            clearDragSelection();
            paint();
          } else if (col === 0) {
            const tr = cell.parentElement;
            for (const cell of getElements(tr)) {
              const c = cell.cellIndex;
              state.cmdSelected.add(cellKey(row, c));
            }
            clearDragSelection();
            paint();
          }
          vm.changeSelectedCount(getSelectedCells());
        }

        function getSelectedCells() {
          return [...getElements()].filter((cell) => SELECTED_CLASSES.every((cls) => cell.classList.contains(cls)));
        }

        function onClear() {
          state.cmdSelected.clear();
          clearDragSelection();
          paint();
          vm.changeSelectedCount(0);
        }

        function onCopy(e) {
          const selected = [...getElements()].filter((cell) =>
            SELECTED_CLASSES.every((cls) => cell.classList.contains(cls)),
          );
          if (!selected.length) {
            return;
          }

          const mat = {};
          for (const cell of selected) {
            const row = getGlobalRowIndex(cell.parentElement);
            const col = cell.cellIndex;
            if (!mat[row]) {
              mat[row] = {};
            }
            mat[row][col] = cell.innerText.trim();
          }

          const tsv = Object.keys(mat)
            .map(Number)
            .sort((a, b) => a - b)
            .map((r) =>
              Object.keys(mat[r])
                .map(Number)
                .sort((a, b) => a - b)
                .map((c) => mat[r][c])
                .join('\t'),
            )
            .join('\n');

          navigator.clipboard
            .writeText(tsv)
            .then(() => {
              vm.onSuccessCopy();
            })
            .catch((err) => {
              console.warn('Copy failed:', err);
            });
          e.preventDefault();
        }

        function onKeyDown(e) {
          if (isModifierKey(e) && e.code === 'KeyC') {
            onCopy(e);
          }
        }

        el.addEventListener('mousedown', onMouseDown);
        el.addEventListener('mouseover', onMouseOver);
        el.addEventListener('dblclick', onDblClick);
        document.addEventListener('mouseup', onMouseUp);
        el.addEventListener('click', onClick);
        document.addEventListener('keydown', onKeyDown);

        // store handlers for cleanup
        el.__selectableHandlers = {
          onMouseDown,
          onMouseOver,
          onDblClick,
          onMouseUp,
          onClick,
          onKeyDown,
        };

        function isMobile() {
          return /Mobi|Android|iPhone|iPad|iPod|BlackBerry|IEMobile|Opera Mini/i.test(navigator.userAgent);
        }

        if (isMobile()) {
          el.removeEventListener('mousedown', onMouseDown);
          el.removeEventListener('mouseover', onMouseOver);
          el.removeEventListener('dblclick', onDblClick);
          document.removeEventListener('mouseup', onMouseUp);
          el.removeEventListener('click', onClick);
          document.removeEventListener('keydown', onKeyDown);
        }
      },

      unmounted(el) {
        const h = el.__selectableHandlers;
        if (h) {
          el.removeEventListener('mousedown', h.onMouseDown);
          el.removeEventListener('mouseover', h.onMouseOver);
          el.removeEventListener('dblclick', h.onDblClick);
          document.removeEventListener('mouseup', h.onMouseUp);
          el.removeEventListener('click', h.onClick);
          document.removeEventListener('keydown', h.onKeyDown);
        }
        delete el.__selectableHandlers;
        delete el.__selectableState;
      },
    });
  },
};
